import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/API/apiClient";

const FacebookConnectPage: React.FC = () => {
  const { user, openAuthModal } = useAuth();
  const [status, setStatus] = useState<{ isConnected: boolean; accountName?: string } | null>(null);
  const [pages, setPages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPages, setLoadingPages] = useState(false);

  useEffect(() => {
    if (user?.sub) {
      fetchStatus();
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (status?.isConnected && user?.sub) {
      fetchPages();
    }
  }, [status, user]);

  const fetchPages = async () => {
    setLoadingPages(true);
    try {
      const response = await apiClient<any[]>(`/api/facebook/pages?userId=${user?.sub}`, { apiType: 2 });
      setPages(response.data || []);
    } catch (error) {
      console.error("Failed to fetch Facebook pages:", error);
    } finally {
      setLoadingPages(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await apiClient<{ isConnected: boolean; accountName?: string }>(
        `/api/facebook/status?userId=${user?.sub}`,
        { apiType: 2 }
      );
      setStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Facebook status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    if (!user) {
      openAuthModal();
      return;
    }
    const backendUrl = "https://socialmediawebappdotnet-c5gvgzgqauekgmej.westindia-01.azurewebsites.net";
    window.location.href = `${backendUrl}/api/facebook/connect?userId=${user.sub}`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect your Facebook account?")) return;
    
    try {
      await apiClient(`/api/facebook/disconnect?userId=${user?.sub}`, {
        method: "DELETE",
        apiType: 2,
      });
      setStatus({ isConnected: false });
    } catch (error) {
      console.error("Failed to disconnect Facebook:", error);
      alert("Failed to disconnect. Please try again.");
    }
  };

  if (loading) {
    return <div style={{ padding: "40px", textAlign: "center" }}>Checking connection status...</div>;
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ color: "#714B67", marginBottom: "20px", fontWeight: 800 }}>Facebook Integration</h2>

      <div style={{
        background: "#fff",
        padding: "30px",
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
        maxWidth: "500px"
      }}>
        {status?.isConnected ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
              <div style={{ 
                width: "50px", 
                height: "50px", 
                borderRadius: "50%", 
                background: "#1877F2", // Facebook Blue
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                color: "#fff",
                marginRight: "15px",
                fontSize: "20px",
                fontWeight: "bold"
              }}>
                f
              </div>
              <div>
                <h3 style={{ margin: 0, color: "#2D3748" }}>{status.accountName || "Facebook Account"}</h3>
                <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>Connected</p>
              </div>
            </div>
            <p style={{ color: "#4A5568", fontSize: "15px", lineHeight: "1.6" }}>
              Your Facebook account is successfully linked. You can now manage your post schedules and view analytics directly from SocialFlow.
            </p>
            <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
              <button
                onClick={handleConnect}
                style={{
                  flex: 1,
                  padding: "12px 20px",
                  borderRadius: "10px",
                  border: "2px solid #714B67",
                  background: "transparent",
                  color: "#714B67",
                  cursor: "pointer",
                  fontWeight: 700,
                  transition: "all 0.2s"
                }}
              >
                Reconnect
              </button>
              <button
                onClick={handleDisconnect}
                style={{
                  flex: 1,
                  padding: "12px 20px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#FEE2E2",
                  color: "#DC2626",
                  cursor: "pointer",
                  fontWeight: 700,
                  transition: "all 0.2s"
                }}
              >
                Disconnect
              </button>
            </div>
            
            {/* Pages Section */}
            <div style={{ marginTop: "40px" }}>
              <h3 style={{ color: "#2D3748", marginBottom: "16px", borderBottom: "1px solid #E2E8F0", paddingBottom: "10px" }}>Your Facebook Pages</h3>
              {loadingPages ? (
                <p style={{ color: "#666" }}>Loading pages...</p>
              ) : pages.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {pages.map(page => (
                    <div key={page.id} style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #E2E8F0",
                      background: "#F7FAFC"
                    }}>
                      {page.picture?.data?.url && (
                        <img 
                          src={page.picture.data.url} 
                          alt={page.name} 
                          style={{ width: "48px", height: "48px", borderRadius: "50%", marginRight: "16px", objectFit: "cover" }} 
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0, color: "#2D3748", fontSize: "16px" }}>{page.name}</h4>
                        <p style={{ margin: "4px 0 0 0", color: "#718096", fontSize: "13px" }}>
                          {page.category} • {page.followers_count} followers
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#666" }}>No pages found. Make sure you have granted page permissions.</p>
              )}
            </div>
          </div>
        ) : (
          <div>
            <h3 style={{ marginBottom: "15px", color: "#2D3748" }}>Not Connected</h3>
            <p style={{ color: "#666", fontSize: "15px", lineHeight: "1.6", marginBottom: "25px" }}>
              Connect your Facebook account to SocialFlow to automate your post scheduling and access page analytics.
            </p>
            <button
               onClick={handleConnect}
              style={{
                padding: "12px 24px",
                borderRadius: "8px",
                border: "none",
                background: "#714B67",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "16px"
              }}
            >
              Connect Facebook Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FacebookConnectPage;
